export const ACCESS_KEY = 'pacilo.user.access_token';
export const REFRESH_KEY = 'pacilo.user.refresh_token';
export const LOGGED_OUT_KEY = 'pacilo.user.logged_out';
export const ADMIN_KEY_PREFIX = 'pacilo.admin.';

export class TokenPersistenceError extends Error {
  constructor() {
    super('Browser storage is unavailable.');
    this.name = 'TokenPersistenceError';
    this.code = 'TOKEN_PERSISTENCE';
  }
}

function persistenceError() {
  return new TokenPersistenceError();
}

function readStorage(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeVerified(storage, key, value) {
  try {
    if (value == null) {
      storage.removeItem(key);
      if (storage.getItem(key) != null) {
        throw persistenceError();
      }
      return;
    }
    storage.setItem(key, value);
    if (storage.getItem(key) !== value) {
      throw persistenceError();
    }
  } catch (error) {
    if (error instanceof TokenPersistenceError) {
      throw error;
    }
    throw persistenceError();
  }
}

function nextTokenValue(incoming, previous) {
  if (incoming === undefined) {
    return previous;
  }
  if (incoming === null) {
    return null;
  }
  if (typeof incoming === 'string' && incoming.trim() !== '') {
    return incoming;
  }
  if (typeof incoming === 'string') {
    return previous;
  }
  throw persistenceError();
}

function forgetLegacyLocalRefresh() {
  try {
    if (readStorage(localStorage, REFRESH_KEY)) {
      writeVerified(localStorage, REFRESH_KEY, null);
    }
  } catch {
    // Legacy localStorage cleanup must not fail session restoration.
  }
}

export function getAccessToken() {
  return readStorage(sessionStorage, ACCESS_KEY);
}

export function getRefreshToken() {
  forgetLegacyLocalRefresh();
  return readStorage(sessionStorage, REFRESH_KEY);
}

export function setTokens({ accessToken, refreshToken } = {}) {
  const previousAccess = readStorage(sessionStorage, ACCESS_KEY);
  const previousRefresh = readStorage(sessionStorage, REFRESH_KEY);
  const nextAccess = nextTokenValue(accessToken, previousAccess);
  const nextRefresh = nextTokenValue(refreshToken, previousRefresh);

  try {
    writeVerified(sessionStorage, ACCESS_KEY, nextAccess);
    writeVerified(sessionStorage, REFRESH_KEY, nextRefresh);
    forgetLegacyLocalRefresh();
  } catch (error) {
    try {
      writeVerified(sessionStorage, ACCESS_KEY, previousAccess);
      writeVerified(sessionStorage, REFRESH_KEY, previousRefresh);
    } catch {
      // Best-effort rollback; the original persistence error is rethrown.
    }
    throw error instanceof TokenPersistenceError ? error : persistenceError();
  }

  return {
    accessToken: readStorage(sessionStorage, ACCESS_KEY),
    refreshToken: readStorage(sessionStorage, REFRESH_KEY),
  };
}

export function applyTokenPayload(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return data;
  }
  const patch = {};
  if (Object.prototype.hasOwnProperty.call(data, 'access_token')) {
    patch.accessToken = data.access_token == null ? null : data.access_token;
  }
  if (Object.prototype.hasOwnProperty.call(data, 'refresh_token')) {
    patch.refreshToken = data.refresh_token == null ? null : data.refresh_token;
  }
  if (Object.keys(patch).length > 0) {
    setTokens(patch);
  }
  return data;
}

export function setAccessToken(token) {
  return setTokens({ accessToken: token || null });
}

export function clearTokens() {
  const previousAccess = readStorage(sessionStorage, ACCESS_KEY);
  const previousRefresh = readStorage(sessionStorage, REFRESH_KEY);
  try {
    writeVerified(sessionStorage, ACCESS_KEY, null);
    writeVerified(sessionStorage, REFRESH_KEY, null);
    forgetLegacyLocalRefresh();
  } catch (error) {
    try {
      writeVerified(sessionStorage, ACCESS_KEY, previousAccess);
      writeVerified(sessionStorage, REFRESH_KEY, previousRefresh);
    } catch {
      // Best-effort rollback.
    }
    throw error instanceof TokenPersistenceError ? error : persistenceError();
  }
}

export function markLoggedOut() {
  try {
    writeVerified(sessionStorage, LOGGED_OUT_KEY, '1');
  } catch {
    // Intentional logout still proceeds if the redirect marker cannot be stored.
  }
}

export function isLoggedOutRedirect() {
  return readStorage(sessionStorage, LOGGED_OUT_KEY) === '1';
}

export function clearLoggedOutRedirect() {
  try {
    writeVerified(sessionStorage, LOGGED_OUT_KEY, null);
  } catch {
    try {
      sessionStorage.removeItem(LOGGED_OUT_KEY);
    } catch {
      // Ignore teardown failures after a successful login.
    }
  }
}

export function clearAccessToken() {
  return setTokens({ accessToken: null });
}

export function hasStoredSession() {
  return Boolean(getAccessToken() || getRefreshToken());
}

export function hasStoredTokenKeys() {
  return {
    access: Boolean(getAccessToken()),
    refresh: Boolean(getRefreshToken()),
    loggedOut: isLoggedOutRedirect(),
  };
}
